<template>
  <div class="card-rent-link-list">
    <div v-if="rentals.length === 0" class="card-rent-link-list-empty">
      <i class="fa-regular fa-file-lines"></i>
      <span>Nenhuma locação vinculada!</span>
    </div>

    <div v-else class="card-rent-link-list-items">
      <div
        v-for="item in rentals"
        :key="item.id"
        class="card-rent-link-list-item"
      >
        <div class="card-rent-link-list-item-left">
          <div class="card-rent-link-list-number">{{ item.numberAndDate }}</div>
          <div class="card-rent-link-list-consultant">
            <i class="fa-solid fa-user-tie"></i>
            {{ item.consultant }}
          </div>
        </div>

        <div class="card-rent-link-list-item-main" @click="navigateToRent(item)">
          <div class="card-rent-link-list-name">{{ item.fullName }}</div>
          <div class="card-rent-link-list-id">{{ item.identification }}</div>
        </div>

        <div class="card-rent-link-list-item-right">
          <div class="card-rent-link-list-item-badges">
            <span class="card-rent-link-list-badge card-rent-link-list-badge-budget" v-if="item.statusRentalName === 'Orçamento'">Orçamento</span>
            <span class="card-rent-link-list-badge card-rent-link-list-badge-approved" v-if="item.statusRentalName === 'Aprovada'">Aprovada</span>
            <span class="card-rent-link-list-badge card-rent-link-list-badge-disapproved" v-if="item.statusRentalName === 'Reprovada'">Reprovada</span>
          </div>
          <div v-if="hasRule('Rental') && showValue" class="card-rent-link-list-value">
            {{ item.totalGeneral | currency }}
          </div>
        </div>

        <div class="card-rent-link-list-item-remove" @click="removeRent({ id: item.id })">
          <Loading v-if="isLoading(item.id)" type="bubbles" :center="false" :width="20" :height="20" />
          <i v-else class="fa-solid fa-trash"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from "@/ui/forms/Button";
import Loading from "@/ui/shared/Loading.vue";

import CardService from "../../services/card.service";

import { mapGetters, mapMutations } from "vuex";

export default {
  name: "CardRentLinkList",
  components: { Button, Loading },
  props: {
    cardId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      rentals: [],
      showValue: false,
    };
  },
  mounted() {
    this.loadRentals();
  },
  computed: {
    ...mapGetters("generic", ["event", "isLoading"]),
    ...mapGetters("user", ["hasRule"]),
  },
  methods: {
    ...mapMutations("generic", ["addLoading", "removeLoading", "hideSideBar"]),
    async loadRentals() {
      const response = await CardService.getAllRentLinked(this.cardId);
      this.rentals = response.data.content.data || [];
      this.$emit("count-change", this.rentals.length);
    },
    async removeRent(item) {
      if (this.isLoading(item.id)) return;
      this.addLoading(item.id);
      try {
        await CardService.removeRentLinked(this.cardId, item.id);
        this.rentals = this.rentals.filter((rent) => rent.id !== item.id);
        this.$emit("count-change", this.rentals.length);
      } finally {
        this.removeLoading([item.id]);
      }
    },
    navigateToRent(item) {
      this.hideSideBar();
      if (this.hasRule("Rental")) {
        this.$router.push({ name: "rentUpdate", params: { id: item.id, type: 1 } });
      } else if (this.hasRule("Operational")) {
        this.$router.push({ name: "rentOperationalList" });
      }
    },
  },
  watch: {
    event: {
      handler(event) {
        if (event.name === "rentLinkedAdded") this.loadRentals();
        if (event.name === "cardSelectedEdit") this.showValue = event.data.showValue;
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.card-rent-link-list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 0;
  color: var(--text-muted);
  font-size: 14px;
  font-style: italic;
}

.card-rent-link-list-empty i {
  font-size: 28px;
}

.card-rent-link-list-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.card-rent-link-list-item {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  background: var(--bg-base);
  padding: 8px 12px;
  transition: background 0.15s;
}

.card-rent-link-list-item:hover {
  background: var(--bg-hover);
}

.card-rent-link-list-item-left {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex-shrink: 0;
  min-width: 100px;
}

.card-rent-link-list-number {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
}

.card-rent-link-list-consultant {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 110px;
}

.card-rent-link-list-consultant i {
  font-size: 11px;
  flex-shrink: 0;
}

.card-rent-link-list-item-main {
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.card-rent-link-list-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-brand);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: opacity 0.15s;
}

.card-rent-link-list-item-main:hover .card-rent-link-list-name {
  text-decoration: underline;
  opacity: 0.8;
}

.card-rent-link-list-id {
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-rent-link-list-item-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.card-rent-link-list-item-badges {
  display: flex;
  justify-content: flex-end;
}

.card-rent-link-list-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  color: var(--text-inverse);
}

.card-rent-link-list-badge-approved {
  background: var(--color-success);
}

.card-rent-link-list-badge-budget {
  background: var(--color-info);
}

.card-rent-link-list-badge-disapproved {
  background: var(--color-danger);
}

.card-rent-link-list-value {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
}

.card-rent-link-list-item-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--color-danger);
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s;
  font-size: 14px;
}

.card-rent-link-list-item-remove:hover {
  opacity: 0.7;
}
</style>
